import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabaseClient";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

const SortableSponsorCard = ({ sponsor, onEdit, onToggleVisibility }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sponsor.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`sponsor-card ${isDragging ? "dragging" : ""}`}
    >
      <div
        className="drag-handle"
        {...attributes}
        {...listeners}
        style={{ cursor: "grab", touchAction: "none" }}
      >
        <i className="fi fi-rs-dots-vertical-double"></i> ⋮⋮
      </div>

      <div
        className="sponsor-logo-wrapper"
        style={{ backgroundColor: sponsor.bg_color }}
      >
        <img src={sponsor.logo_url} alt={sponsor.name} />
      </div>

      <div className="sponsor-details">
        <div className="sponsor-meta">
          <span className="sponsor-name">{sponsor.name}</span>
          <button
            className="edit-icon-btn"
            onClick={() => onEdit(sponsor)}
            title="Uredi sponzora"
          >
            ⚙️
          </button>
        </div>

        <div className="sponsor-actions">
          <span className="status-text">
            {sponsor.is_visible ? "Prikazano" : "Skriveno"}
          </span>
          <div className="switch-wrapper">
            <label className="switch">
              <input
                type="checkbox"
                checked={sponsor.is_visible}
                onChange={() =>
                  onToggleVisibility(sponsor.id, sponsor.is_visible)
                }
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// GLAVNA KOMPONENTA
// ==========================================
const AdminSettingsSponsors = ({ onBack, turnirId }) => {
  const [sponsors, setSponsors] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [loading, setLoading] = useState(false);

  // Form states
  const [name, setName] = useState("");
  const [webUrl, setWebUrl] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [bgColor, setBgColor] = useState("#FFFFFF");

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
  );

  const compressImage = (file, maxWidth = 400, maxHeight = 250) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], `${Date.now()}.webp`, {
                  type: "image/webp",
                });
                resolve(compressedFile);
              } else {
                reject(new Error("Kompresija nije uspjela."));
              }
            },
            "image/webp",
            0.8,
          );
        };
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const fetchSponsors = useCallback(async () => {
    if (!turnirId) return;

    const { data, error } = await supabase
      .from("admin_sponzori")
      .select("*")
      .eq("turnir_id", turnirId)
      .order("position", { ascending: true });

    if (error) console.error("Greška pri dohvaćanju:", error.message);
    else setSponsors(data || []);
  }, [turnirId]);

  useEffect(() => {
    fetchSponsors();
  }, [fetchSponsors]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setSponsors((items) => {
      const oldIndex = items.findIndex((i) => i.id.toString() === active.id);
      const newIndex = items.findIndex((i) => i.id.toString() === over.id);

      const reordered = arrayMove(items, oldIndex, newIndex);

      const updatedItems = reordered.map((item, index) => ({
        ...item,
        position: index,
      }));

      const payload = updatedItems.map((item) => ({
        id: item.id,
        name: item.name,
        logo_url: item.logo_url,
        bg_color: item.bg_color,
        is_visible: item.is_visible,
        web_url: item.web_url,
        position: item.position,
        turnir_id: item.turnir_id,
      }));

      supabase
        .from("admin_sponzori")
        .upsert(payload)
        .then(({ error }) => {
          if (error) {
            console.error("Greška pri spremanju redoslijeda:", error.message);
            fetchSponsors();
          }
        });

      return updatedItems;
    });
  };

  const handleAddNew = () => {
    if (!turnirId) {
      alert("Molimo odaberite turnir prije dodavanja sponzora.");
      return;
    }
    setEditingSponsor(null);
    setName("");
    setWebUrl("");
    setLogoFile(null);
    setLogoPreview("");
    setBgColor("#fff");
    setIsModalOpen(true);
  };

  const handleEdit = (sponsor) => {
    setEditingSponsor(sponsor);
    setName(sponsor.name);
    setWebUrl(sponsor.web_url || "");
    setLogoFile(null);
    setLogoPreview(sponsor.logo_url);
    setBgColor(sponsor.bg_color);
    setIsModalOpen(true);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const uploadLogoToStorage = async (file) => {
    const compressedFile = await compressImage(file);
    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `logos/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("sponsor-logos")
      .upload(filePath, compressedFile);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from("sponsor-logos")
      .getPublicUrl(filePath);
    return data.publicUrl;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name) return alert("Naziv sponzora je obavezan!");
    if (!editingSponsor && !logoFile)
      return alert("Logo sponzora je obavezan!");

    setLoading(true);
    try {
      let finalLogoUrl = logoPreview;

      if (logoFile) {
        finalLogoUrl = await uploadLogoToStorage(logoFile);
      }

      const sponsorData = {
        name,
        web_url: webUrl || null,
        logo_url: finalLogoUrl,
        bg_color: bgColor,
        turnir_id: turnirId,
      };

      if (editingSponsor) {
        const { error } = await supabase
          .from("admin_sponzori")
          .update(sponsorData)
          .eq("id", editingSponsor.id);

        if (error) throw error;
      } else {
        const { error } = await supabase.from("admin_sponzori").insert([
          {
            ...sponsorData,
            is_visible: true,
            position: sponsors.length,
          },
        ]);

        if (error) throw error;
      }

      setIsModalOpen(false);
      fetchSponsors();
    } catch (error) {
      alert("Greška pri spremanju: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Jeste li sigurni da želite obrisati ovog sponzora?"))
      return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from("admin_sponzori")
        .delete()
        .eq("id", id);
      if (error) throw error;

      setIsModalOpen(false);
      fetchSponsors();
    } catch (error) {
      alert("Greška pri brisanju: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = async (id, currentStatus) => {
    try {
      setSponsors((prev) =>
        prev.map((s) =>
          s.id === id ? { ...s, is_visible: !currentStatus } : s,
        ),
      );

      const { error } = await supabase
        .from("admin_sponzori")
        .update({ is_visible: !currentStatus })
        .eq("id", id);

      if (error) throw error;
    } catch (error) {
      alert("Greška pri promjeni statusa: " + error.message);
      fetchSponsors();
    }
  };

  return (
    <div className="admin-sponsors-container">
      <button className="back-btn" onClick={onBack}>
        <i className="fi fi-rs-arrow-left"></i> Natrag
      </button>
      <div className="sponsors-header">
        <h2>Upravljanje Sponzorima za ovaj turnir</h2>
        <button
          className="add-sponsor-btn"
          onClick={handleAddNew}
          disabled={loading || !turnirId}
        >
          + DODAJ NOVOG SPONZORA
        </button>
      </div>

      {!turnirId ? (
        <div className="no-sponsors">Molimo odaberite turnir.</div>
      ) : sponsors.length === 0 ? (
        <div className="no-sponsors">
          Trenutno nema dodanih sponzora za ovaj turnir.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={sponsors.map((s) => s.id.toString())}
            strategy={verticalListSortingStrategy}
          >
            <div className="sponsors-list">
              {sponsors.map((sponsor) => (
                <SortableSponsorCard
                  key={sponsor.id}
                  sponsor={sponsor}
                  onEdit={handleEdit}
                  onToggleVisibility={toggleVisibility}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* MODAL ZA UNOS / UREĐIVANJE */}
      {isModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => !loading && setIsModalOpen(false)}
        >
          <div className="sponsor-modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              {editingSponsor ? "Uredi Sponzora" : "Dodaj Novog Sponzora"}
            </h3>

            <form onSubmit={handleSave}>
              <div className="form-group">
                <label>Naziv Sponzora</label>
                <input
                  type="text"
                  placeholder="Npr. Nike"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Web stranica / Društvena mreža (URL)</label>
                <input
                  type="url"
                  placeholder="https://example.com"
                  value={webUrl}
                  onChange={(e) => setWebUrl(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="form-row">
                <div className="form-group file-input-group">
                  <label>Učitaj Logo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    id="file-upload"
                    disabled={loading}
                  />
                  <label htmlFor="file-upload" className="custom-file-btn">
                    Odaberi sliku
                  </label>
                </div>

                <div className="form-group color-input-group">
                  <label>Boja Pozadine (Match)</label>
                  <div className="color-picker-wrapper">
                    <input
                      type="color"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      disabled={loading}
                    />
                    <span>{bgColor.toUpperCase()}</span>
                  </div>
                </div>
              </div>

              <div className="preview-section">
                <label>Pregled izgleda (Preview)</label>
                <div
                  className="live-preview-box"
                  style={{ backgroundColor: bgColor }}
                >
                  {logoPreview ? (
                    <img src={logoPreview} alt="Preview" />
                  ) : (
                    <span className="no-image-text">
                      Nema odabranog logotipa
                    </span>
                  )}
                </div>
              </div>

              <div className="modal-buttons">
                <button type="submit" className="save-btn" disabled={loading}>
                  {loading ? "SPREMANJE..." : "SPREMI SPONZORA"}
                </button>

                {editingSponsor && (
                  <button
                    type="button"
                    className="delete-btn"
                    onClick={() => handleDelete(editingSponsor.id)}
                    disabled={loading}
                  >
                    {loading ? "BRISANJE..." : "OBRIŠI SPONZORA"}
                  </button>
                )}

                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setIsModalOpen(false)}
                  disabled={loading}
                >
                  Odustani
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettingsSponsors;
